import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, CheckCircle, Smartphone, Users, BarChart3, Clock, Shield, Zap } from 'lucide-react';

const Home = () => {
  const stats = [
    { value: '5x', label: 'More Bookings Managed' },
    { value: '99.9%', label: 'Error-Free Dispatching' },
    { value: '24/7', label: 'Digital Availability' },
    { value: '100%', label: 'Customizable Platform' },
  ];

  const features = [
    {
      icon: <Smartphone className="w-8 h-8 text-red-600" />,
      title: 'Mobile Apps (iOS & Android)',
      description: 'Native mobile applications for seamless booking experiences for both corporates and individual travelers.',
    },
    {
      icon: <Users className="w-8 h-8 text-red-600" />,
      title: 'Vendor Management',
      description: 'Comprehensive vendor management system to streamline partnerships and operations across your network.',
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-red-600" />,
      title: 'Admin Panel & Dashboards',
      description: 'Powerful admin control center with personalized dashboards for complete operational visibility.',
    },
    {
      icon: <Clock className="w-8 h-8 text-red-600" />,
      title: 'Automated Operations',
      description: 'Automate bookings, invoicing, and reduce manual work by up to 80% with intelligent automation.',
    },
    {
      icon: <Shield className="w-8 h-8 text-red-600" />,
      title: 'Employee Transportation Services',
      description: 'Complete ETS platform designed specifically for corporate travel management and compliance.',
    },
    {
      icon: <Zap className="w-8 h-8 text-red-600" />,
      title: 'Fleet Management',
      description: 'Comprehensive fleet management with vehicle maintenance, compliance tracking, and driver management.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-white py-20 px-4" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-testid="hero-title">
              Transform Your Travel Business with{' '}
              <span className="text-red-600">Frogleap Technologies</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8" data-testid="hero-description">
              A comprehensive SaaS platform for travel partners offering On call, Employee Transportation Services (ETS),
              vendor management, fleet management, and seamless booking solutions for corporates and individuals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white" data-testid="hero-book-demo-btn">
                  Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete Product Ecosystem</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to run a high-volume travel operation with automation, efficiency, and growth at the core.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid={`feature-card-${index}`}>
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-red-600 text-red-600 hover:bg-red-50" data-testid="view-all-features-btn">
                View All Features <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 bg-white" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="text-center" data-testid="value-prop-productivity">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Skyrocket Productivity</h3>
              <p className="text-gray-600">
                Automate manual dispatching and phone bookings. Allow your team to manage five times the volume
                without hiring additional operators.
              </p>
            </div>

            <div className="text-center" data-testid="value-prop-trust">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Build Unshakable Trust</h3>
              <p className="text-gray-600">
                Provide absolute transparency to your corporate clients with real-time tracking and automated,
                error-free digital billing.
              </p>
            </div>

            <div className="text-center" data-testid="value-prop-presence">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Own Your Digital Presence</h3>
              <p className="text-gray-600">
                Stop losing customers to aggregators. With your own branded apps on iOS and Android, you stay
                top-of-mind and available 24/7/365.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Travel Business?</h2>
          <p className="text-xl mb-8 text-red-100">
            Join forward-thinking travel partners upgrading their operations with Frogleap Technologies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100" data-testid="cta-book-demo-btn">
                Book Your Free Demo Today <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;